/**
 * src/modules/perfil-helpers.js
 *
 * Helpers utility extraídos del IIFE inline de index.html en sub-fase 3.16.
 *
 * Migración byte-a-byte de 8 helpers que el IIFE producía como bridges
 * window.X y eran consumidos por src/modules/perfil.js (vivo),
 * perfil-prode.js (NUEVO) y perfil-tribuna.js (NUEVO).
 *
 * Wraps defensivos preservados (typeof guards con fallback) por paridad
 * funcional. Cero refactor interno.
 *
 * Exports:
 *   - formatPM(value): wrap defensivo de formatNumber con fallback Intl.
 *   - escapePM(value): escape HTML genérico.
 *   - readDB(): wrap defensivo de getDB con fallback localStorage parse.
 *   - profileData(codigo): retorna {db, grupo, jugador} consolidado.
 *   - activeGroupBarPM(codigo): wrap defensivo de renderActiveGroupBar.
 *   - bottomNavPM(codigo): wrap defensivo de renderBottomNav.
 *   - historyCardsPM(): retorna HTML de 4 cards mock de historial.
 *   - renderSavedTribunaPM(codigo): HTML de arengas guardadas en localStorage.
 *
 * ensureProfileStyles ELIMINADA: el CSS que inyectaba dinámicamente
 * (~210 líneas) se migró a css/perfil-runtime.css y se carga estático
 * vía <link rel="stylesheet"> en index.html.
 */

import { getDB } from '../core/db.js';
import { renderActiveGroupBar, renderBottomNav } from '../ui/components.js';
import { formatNumber } from '../utils/format.js';

export const formatPM = (value) => {
    try {
        return typeof formatNumber === 'function'
            ? formatNumber(value)
            : new Intl.NumberFormat('es-UY').format(value);
    } catch (error) {
        return String(value);
    }
};

export const escapePM = (value = '') => String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

export const readDB = () => {
    try {
        if (typeof getDB === 'function') return getDB();
    } catch (error) {}
    try {
        return JSON.parse(localStorage.getItem('pacto_db') || '{"grupos":{},"usuarios":{}}');
    } catch (error) {
        return { grupos: {}, usuarios: {} };
    }
};

export const profileData = (codigo) => {
    const db = readDB();
    const grupo = db.grupos?.[codigo] || { nombre: 'Pacto Mundial', jugadores: [] };
    const jugadorId = localStorage.getItem(`sesion_${codigo}`) || localStorage.getItem('current_user_id');
    const jugador = (grupo.jugadores || []).find((j) => {
        const id = j.id?.toString();
        const userId = j.user_id?.toString();
        return id === jugadorId?.toString() || userId === jugadorId?.toString();
    }) || (grupo.jugadores || [])[0] || { apodo: 'Jugador Mundial', nombre: '' };
    return { db, grupo, jugador };
};

export const activeGroupBarPM = (codigo) => {
    try {
        return typeof renderActiveGroupBar === 'function' ? renderActiveGroupBar(codigo) : '';
    } catch (error) {
        return '';
    }
};

export const bottomNavPM = (codigo) => {
    try {
        return typeof renderBottomNav === 'function' ? renderBottomNav(codigo, 'perfil') : '';
    } catch (error) {
        return '';
    }
};

// historyCardsPM(resumen) — Mis Arengas / Cábalas / Promesas del perfil.
// Recibe el shape de getResumenRitualesPorTipo(userId):
//   { arenga: {creadas, reaccionesRecibidas, puntos},
//     cabala: {...}, promesa: {..., incumplidas} }
//
// Bug previo (auditoría 29-may): mostraba "0 reacciones" hardcoded y
// "+N pts" = solo creaciones, ignorando la curva. Ahora cada card muestra:
//   - "N publicadas/activas/pendientes · M reacciones"
//   - "+X pts" donde X = creaciones + curva(reacciones) [+ promesas
//     incumplidas para promesa]
// Scoring §3 R1 (creación +1), §5.1 R2 (curva), §7 R8 (-100 incumplida).
export const historyCardsPM = (resumen = {}) => {
    const a = resumen.arenga  || { creadas: 0, reaccionesRecibidas: 0, puntos: 0 };
    const c = resumen.cabala  || { creadas: 0, reaccionesRecibidas: 0, puntos: 0 };
    const p = resumen.promesa || { creadas: 0, reaccionesRecibidas: 0, puntos: 0, incumplidas: 0 };
    const fmtPts = (n) => `${n > 0 ? '+' : ''}${n} pts`;
    const reacLbl = (n) => `${n} ${n === 1 ? 'reacción' : 'reacciones'}`;
    return [
        ['Mis Arengas',     `${a.creadas} publicadas &middot; ${reacLbl(a.reaccionesRecibidas)}`,                            fmtPts(a.puntos)],
        ['Mis C&aacute;balas', `${c.creadas} creadas &middot; ${reacLbl(c.reaccionesRecibidas)}`,                            fmtPts(c.puntos)],
        ['Mis Promesas',    `${p.creadas} creadas &middot; ${reacLbl(p.reaccionesRecibidas)}${p.incumplidas ? ` &middot; ${p.incumplidas} incumplida${p.incumplidas === 1 ? '' : 's'}` : ''}`, fmtPts(p.puntos)],
    ].map(([title, state, points]) => `
        <article class="perfil-history-card">
            <div>
                <strong>${title}</strong>
                <small>${state}</small>
            </div>
            <b>${points}</b>
        </article>
    `).join('');
};

export const renderSavedTribunaPM = (codigo) => {
    let saved = [];
    try {
        saved = JSON.parse(localStorage.getItem(`tribuna_guardados_${codigo}`) || '[]');
    } catch (error) {
        saved = [];
    }
    return `
        <h3 class="perfil-section-title">Mis Guardados de Tribuna</h3>
        <section class="tribuna-library">
            <div class="tribuna-library-head">
                <span>Peque&ntilde;a biblioteca emocional de arengas favoritas</span>
                <b>${formatPM(saved.length)}</b>
            </div>
            ${saved.length
                ? saved.slice(0, 3).map((item) => `
                    <button class="tribuna-saved-item" type="button" onclick="window.location.hash='/tribuna/${codigo}'">
                        ${escapePM(item.titulo || item.texto || 'Arenga guardada')}
                    </button>
                `).join('')
                : `<div class="tribuna-empty">Todav&iacute;a no guardaste arengas de Tribuna.</div>`
            }
        </section>
    `;
};
